import React, { useEffect, useMemo, useState } from 'react';
import { useInterview } from '../../utils/interviewContext';
import { trackPostHogEvent } from '../../utils/posthogUtils';
import { getInterviewFlow, nextStep, previousStep, stepPosition } from '../../utils/interviewFlow';
import { recommendationFor, resultPath } from '../../utils/interviewRecommendation';
import styles from './InterviewPriority.module.css';
import { Collapse } from '@mui/material';
import Footer from '../.shared/footer/footer';
import ProgressBar from '../.shared/ProgressBar/ProgressBar';
import Header from '../.shared/header/header';
import CoinStackIcon from '../CustomIcons/CoinStackIcon';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';

const PATH = '/interview/priority';

export default function InterviewPriority({ goToPreviousStep }) {
    const { updateMultipleFields, interviewData } = useInterview();
    const [ selectedPriority, setSelectedPriority ] = useState('none');

    const navigate = useNavigate();

    // Read once per mount, so chrome and navigation cannot disagree if the
    // stored assignment changes while the page is open.
    const flow = useMemo(getInterviewFlow, []);

    // Priority ends the control funnel, but in the extended one three more
    // questions follow, so the button must not promise results yet.
    const isFinalStep = !nextStep(flow, PATH);

    const prioritiesLabels = {
        affordability: 'Affordability',
        speed: 'Speed',
        results: 'Best Results'
    };

    // Populate from localStorage on component mount
    useEffect(() => {
        if (interviewData.priority) {
            setSelectedPriority(interviewData.priority);
        }
        
        // Track when user reaches the priority step
                trackPostHogEvent('interview_step_viewed', {
                    interview_step: 'priority',
                    step_number: stepPosition(flow, PATH),
                    total_steps: flow.steps.length,
                    interview_flow: flow.id,
                    has_existing_data: !!interviewData.priority
                });
        }, [interviewData.priority, flow]);

    const handleConfirm = (recentlyChosen) => {
        if (selectedPriority === 'none' && !recentlyChosen) return;

        const chosenPriority = recentlyChosen ? recentlyChosen : selectedPriority;

        setSelectedPriority(chosenPriority);

        // Both arms derive the recommendation here, at the moment the user makes
        // the choice that determines it, so an abandoned funnel still leaves a
        // coherent context behind.
        const { medication, frequency: defaultFrequency } = recommendationFor(chosenPriority);
        
        // Timeout to allow button selection animation to complete
        setTimeout(() => {
            // Save to interview context
            updateMultipleFields({
                priority: chosenPriority,
                frequency: defaultFrequency,
                medication: medication
            });
            
            // Track priority selection
            trackPostHogEvent('interview_priority_selected', {
                selected_priority: chosenPriority,
                priority_label: prioritiesLabels[chosenPriority],
                interview_step: 'priority',
                step_number: stepPosition(flow, PATH),
                interview_flow: flow.id
            });
            
            // Track the automatic frequency setting and medication determination
            trackPostHogEvent('interview_frequency_auto_selected', {
                auto_frequency: defaultFrequency,
                determined_medication: medication,
                priority_basis: chosenPriority,
                skipped_frequency_step: true
            });

            // Last step in the control funnel; in the extended funnel three more
            // questions follow before the result page.
            navigate(nextStep(flow, PATH) ?? resultPath({ medication, frequency: defaultFrequency }));
        }, 300);
    };

    const handleBack = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate(previousStep(flow, PATH) ?? '/');
    };

    return (
        <>
            <div className={styles.interviewPageContainer}>
                <Header hideNavigation={flow.hideNavigation} />
                <ProgressBar step={stepPosition(flow, PATH)} totalSteps={flow.steps.length} />
                <Collapse
                    in
                    appear
                    timeout={500}
                    unmountOnExit
                    mountOnEnter
                    orientation="vertical"
                >
                    <div className={styles.priorityContent}>
                        <h1 className={styles.title}>Which is most important for your success?</h1>
                        <div className={styles.buttonsContainer}>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'affordability' ? styles.selected : ''}`} onClick={() => handleConfirm('affordability')}>
                                <CoinStackIcon sx={{ color: `${selectedPriority === 'affordability' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Affordability</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'speed' ? styles.selected : ''}`} onClick={() => handleConfirm('speed')}>
                                <SpeedIcon sx={{ color: `${selectedPriority === 'speed' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Speed</span>
                            </button>

                            <button className={`${styles.interviewButton} ${selectedPriority === 'results' ? styles.selected : ''}`} onClick={() => handleConfirm('results')}>
                                <TrendingUpIcon sx={{ color: `${selectedPriority === 'results' ? '#0D79FE' : '#4E4E4EFD'}` }} />
                                <span>Best Results</span>
                            </button>

                            <div className={styles.confirmationButtons}>
                                <button 
                                    className={styles.secondaryButton}
                                    onClick={handleBack}
                                >
                                    Back
                                </button>
                                <button 
                                    className={`${styles.primaryButton} ${selectedPriority === 'none' ? styles.disabledButton : ''}`}
                                    onClick={() => handleConfirm()}
                                >
                                    {isFinalStep ? 'See My Results' : 'Continue'}
                                </button>
                            </div>

                        </div>
                    </div>
                </Collapse>
            </div>
            <Footer />
        </>
    );
}
